import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { BookOpen, Check, Disc3, Heart, Moon, Sun, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { VerifiedBadge } from "@/components/ui/verified-badge";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { followUser, isFollowing, isProfileLoved, toggleProfileLove, unfollowUser } from "@/lib/social.functions";
import { MadeWithViny } from "../MadeWithViny";
import type { TemplateProps } from "../types";

const MOMENT_LABELS: Record<string, string> = {
  slow_down: "Desacelerar",
  alone: "Ficar sozinho",
  remember: "Lembrar",
  feel: "Sentir",
  inspire: "Me inspirar",
};

type RitualMoment = TemplateProps["bio"]["rituals"][number];

function formatDate(date: string) {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date(date));
}

export function RitualDiaryTemplate({ bio, config, theme = "light" }: TemplateProps) {
  const [selected, setSelected] = useState<RitualMoment | null>(null);
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const followingFn = useServerFn(isFollowing);
  const followFn = useServerFn(followUser);
  const unfollowFn = useServerFn(unfollowUser);
  const lovedFn = useServerFn(isProfileLoved);
  const loveFn = useServerFn(toggleProfileLove);
  const isMe = user?.id === bio.id;
  const isDark = theme === "dark";
  const nextTheme = isDark ? "light" : "dark";
  const name = config.title || bio.display_name || bio.username;
  const avatar = bio.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${bio.username}`;

  const followingQ = useQuery({
    queryKey: ["mini-site-following", bio.id],
    queryFn: () => followingFn({ data: { targetId: bio.id } }),
    enabled: !!user && !isMe,
  });
  const lovedQ = useQuery({
    queryKey: ["mini-site-loved", bio.id],
    queryFn: () => lovedFn({ data: { targetId: bio.id } }),
    enabled: !!user && !isMe,
  });
  const followMutation = useMutation({
    mutationFn: () => followingQ.data?.following
      ? unfollowFn({ data: { targetId: bio.id } })
      : followFn({ data: { targetId: bio.id } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["mini-site-following", bio.id] }),
  });
  const loveMutation = useMutation({
    mutationFn: () => loveFn({ data: { targetId: bio.id } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["mini-site-loved", bio.id] });
      void queryClient.invalidateQueries({ queryKey: ["public-bio", bio.username] });
    },
  });
  const requireAccount = (action: () => void) => user
    ? action()
    : navigate({ to: "/auth", search: { next: `/${bio.username}` } });

  const pageTone = isDark ? "bg-neutral-950 text-neutral-100" : "bg-stone-100 text-neutral-900";
  const mutedTone = isDark ? "text-neutral-400" : "text-neutral-500";
  const lineTone = isDark ? "border-white/10" : "border-neutral-900/10";
  const cardTone = isDark ? "bg-neutral-900 hover:bg-neutral-800" : "bg-stone-50 hover:bg-white";
  const buttonTone = isDark
    ? "border-white/15 bg-transparent text-neutral-100 hover:bg-white/10"
    : "border-neutral-900/15 bg-transparent text-neutral-900 hover:bg-neutral-900/5";

  return (
    <div className={`min-h-screen transition-colors ${pageTone}`}>
      <div className="lg:grid lg:grid-cols-[320px_1fr]">
        <aside className={`px-6 pt-8 sm:px-8 sm:pt-12 lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col lg:justify-between lg:border-r lg:py-12 ${lineTone}`}>
          <div>
            <div className="flex items-center gap-3">
              <img src={avatar} alt={name} loading="lazy" decoding="async" className={`h-12 w-12 rounded-full object-cover ring-1 ${isDark ? "ring-white/10" : "ring-neutral-900/10"}`} />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 truncate font-display text-base font-semibold">
                  {name}
                  {bio.is_premium && <VerifiedBadge variant="shimmer" size={16} aria-label="Verificado" />}
                </div>
                <div className={`truncate text-xs ${mutedTone}`}>@{bio.username}</div>
              </div>
              <Link
                to="/$username"
                params={{ username: bio.username }}
                search={(previous) => ({ ...previous, t: nextTheme })}
                replace
                aria-label={isDark ? "Usar modo claro" : "Usar modo escuro"}
                title={isDark ? "Modo claro" : "Modo escuro"}
                className={`ml-auto grid h-10 w-10 shrink-0 place-items-center rounded-full border transition-colors ${buttonTone}`}
              >
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Link>
            </div>

            <h1 className="mt-10 font-display text-5xl font-medium leading-none sm:text-6xl">Momentos</h1>
            {(config.subtitle || bio.bio) && (
              <p className={`mt-6 max-w-xs text-sm leading-relaxed ${mutedTone}`}>{config.subtitle || bio.bio}</p>
            )}

            <div className="mt-8 flex gap-6">
              <div><strong className="font-display text-2xl font-medium">{bio.rituals.length}</strong><span className={`block text-[10px] uppercase ${mutedTone}`}>Momentos</span></div>
              <div><strong className="font-display text-2xl font-medium">{bio.counts.followers}</strong><span className={`block text-[10px] uppercase ${mutedTone}`}>Seguidores</span></div>
              <div><strong className="font-display text-2xl font-medium">{bio.counts.loves}</strong><span className={`block text-[10px] uppercase ${mutedTone}`}>Amei</span></div>
            </div>

            {!isMe && (
              <div className="mt-8 flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => requireAccount(() => followMutation.mutate())} className={`rounded-full ${buttonTone}`}>
                  {followingQ.data?.following ? <Check /> : <UserPlus />}
                  {followingQ.data?.following ? "Seguindo" : "Seguir"}
                </Button>
                <Button variant="outline" onClick={() => requireAccount(() => loveMutation.mutate())} className={`rounded-full ${buttonTone}`}>
                  <Heart className={lovedQ.data?.loved ? "fill-current text-primary" : ""} /> Amei
                </Button>
              </div>
            )}
          </div>

          <div className="mt-12 hidden lg:block"><MadeWithViny variant={isDark ? "light" : "dark"} /></div>
        </aside>

        <main className="px-6 py-12 sm:px-8 lg:px-10 lg:py-14 xl:pr-12">
          <div className={`mb-6 flex items-baseline gap-3 border-b pb-3 ${lineTone}`}>
            <h2 className="inline-flex items-center gap-2 font-display text-xl font-medium"><BookOpen className="h-4 w-4" /> Diário</h2>
            <span className={`text-xs uppercase ${mutedTone}`}>{bio.rituals.length} {bio.rituals.length === 1 ? "registro" : "registros"}</span>
          </div>

          {bio.rituals.length ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {bio.rituals.map((moment) => (
                <Button
                  key={moment.id}
                  type="button"
                  variant="ghost"
                  onClick={() => setSelected(moment)}
                  className={`group h-auto min-h-0 flex-col items-stretch justify-start overflow-hidden rounded-lg p-0 text-left shadow-none transition-all hover:-translate-y-0.5 ${cardTone}`}
                >
                  <div className="grid grid-cols-[7rem_1fr] items-stretch sm:block">
                    {moment.cover_url ? (
                      <img src={moment.cover_url} alt={moment.album_title} loading="lazy" decoding="async" className="aspect-square h-full w-full object-cover sm:h-auto" />
                    ) : (
                      <div className={`grid aspect-square place-items-center ${isDark ? "bg-white/5" : "bg-neutral-900/5"}`}><Disc3 className="h-8 w-8 opacity-40" /></div>
                    )}
                    <div className="min-w-0 p-4 sm:p-5">
                      <p className={`text-[10px] uppercase ${mutedTone}`}>{formatDate(moment.created_at)}</p>
                      <h3 className="mt-2 truncate font-display text-lg font-semibold">{moment.album_title}</h3>
                      <p className={`truncate text-xs ${mutedTone}`}>{moment.artist}</p>
                      {moment.reflection && <p className={`mt-4 line-clamp-3 whitespace-normal text-sm leading-relaxed ${mutedTone}`}>{moment.reflection}</p>}
                      <span className="mt-5 inline-block text-xs font-medium underline decoration-current/25 underline-offset-4">Ler momento</span>
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          ) : (
            <div className={`grid min-h-80 place-items-center rounded-lg border border-dashed text-sm ${lineTone} ${mutedTone}`}>Nenhum momento publicado ainda.</div>
          )}
        </main>
      </div>

      <footer className={`flex items-center justify-between border-t px-8 py-5 text-[10px] uppercase lg:hidden ${lineTone} ${mutedTone}`}>
        <MadeWithViny variant={isDark ? "light" : "dark"} /><span>Diário de Ritual</span>
      </footer>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        {selected && (
          <DialogContent className={`inset-0 left-0 top-0 flex h-dvh w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden border-0 p-0 sm:rounded-none ${pageTone} [&>button]:right-5 [&>button]:top-5 [&>button]:z-20 [&>button]:grid [&>button]:h-10 [&>button]:w-10 [&>button]:place-items-center [&>button]:rounded-full [&>button]:border [&>button]:border-current/15`}>
            <DialogTitle className="sr-only">{selected.album_title}</DialogTitle>
            <DialogDescription className="sr-only">Reflexão registrada em {formatDate(selected.created_at)}</DialogDescription>
            <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(20rem,38vw)_1fr]">
              <div className={`relative hidden overflow-hidden lg:block ${isDark ? "bg-neutral-900" : "bg-stone-200"}`}>
                {selected.cover_url ? <img src={selected.cover_url} alt={selected.album_title} className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center"><Disc3 className="h-20 w-20 opacity-30" /></div>}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute inset-x-8 bottom-8 text-white">
                  <p className="text-sm text-white/65">{selected.artist}</p>
                  <p className="mt-1 font-display text-3xl font-semibold">{selected.album_title}</p>
                </div>
              </div>
              <article className="min-h-0 overflow-y-auto px-6 pb-16 pt-20 sm:px-12 lg:px-[8vw] lg:py-20">
                <div className="mx-auto max-w-2xl">
                  <p className={`text-xs uppercase ${mutedTone}`}>{formatDate(selected.created_at)} · {MOMENT_LABELS[selected.moment_type] ?? "Ritual"}</p>
                  <h2 className="mt-5 font-display text-4xl font-semibold leading-tight sm:text-5xl">{selected.album_title}</h2>
                  <p className={`mt-2 text-base ${mutedTone}`}>{selected.artist}</p>
                  {selected.cover_url && <img src={selected.cover_url} alt="" className="mt-8 aspect-square w-full rounded-lg object-cover lg:hidden" />}
                  <div className={`my-10 h-px w-full ${isDark ? "bg-white/10" : "bg-neutral-900/10"}`} />
                  <p className="whitespace-pre-wrap text-lg leading-8 sm:text-xl sm:leading-9">{selected.reflection || "Este momento não tem uma reflexão escrita."}</p>
                </div>
              </article>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}